/**
 * Value-pattern scrubber for free-text strings sent to CloudWatch RUM (#3037).
 *
 * This is a DIFFERENT concern from the key-based `redactForRum` in
 * `rum-redact.ts`. That redactor masks values by their *field name* and so
 * structurally cannot scrub a value embedded in a free-text string — most
 * importantly a native `Error.message` / `Error.stack`, which RUM's
 * `recordError` ships verbatim. This module masks by *value pattern* instead:
 * it regex-matches email addresses and bearer/JWT-like tokens anywhere in a
 * string and replaces them with fixed placeholders.
 *
 * Conservatism is the whole point. The audited throws in the app use static
 * strings plus status codes (e.g. `Request failed with status 404`), which must
 * pass through UNCHANGED — over-masking would corrupt otherwise-clean
 * diagnostics. The patterns below deliberately target only high-confidence PII
 * /secret shapes and leave ordinary words, numbers, and short hex ids alone.
 */

/** Placeholder substituted for any matched email address. */
const EMAIL_PLACEHOLDER = '[REDACTED_EMAIL]';
/** Placeholder substituted for any matched bearer/JWT-like token. */
const TOKEN_PLACEHOLDER = '[REDACTED_TOKEN]';

/** RFC-pragmatic email address (local@domain.tld). */
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;

/**
 * A JWT: three base64url segments separated by dots, with a `eyJ`-prefixed
 * header (the base64url encoding of `{"`). Anchoring on `eyJ` keeps this from
 * matching ordinary dotted identifiers.
 */
const JWT_RE = /eyJ[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+/g;

/**
 * A `Bearer <token>` substring (case-insensitive on the scheme). The token is
 * any run of token characters; only the token is replaced so the surrounding
 * text/structure is preserved.
 */
const BEARER_RE = /(Bearer\s+)[A-Za-z0-9._~+/=-]+/gi;

/**
 * Scrubs high-confidence PII/secret value patterns from a free-text string.
 *
 * Masks email addresses and bearer/JWT-like tokens; everything else — ordinary
 * words, numbers, status codes, short hex ids — is left untouched. Safe to call
 * on any string, including `''`.
 *
 * @param text - The free-text string to scrub (e.g. an `Error.message`).
 * @returns The string with matched patterns replaced by fixed placeholders.
 */
export function scrubValuePatterns(text: string): string {
    return text.replace(JWT_RE, TOKEN_PLACEHOLDER).replace(BEARER_RE, `$1${TOKEN_PLACEHOLDER}`).replace(EMAIL_RE, EMAIL_PLACEHOLDER);
}
